class CustomColorPicker extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('change', this.onVariantChange.bind(this));

        if(this.querySelector("[data-color-container]") !== null ) {
            this.init(); 
        }
    }

    async onVariantChange() {
        const currentColor = this.getCurrentColor();
        if (!currentColor) return;

        const productInfo = this.closest('product-info');
        productInfo?.abortPendingVariantUpdate?.();
        this.setVariantSelectsUpdating(true);

        try {
            this.updateSelectedState();
            this.updateActiveColorLabel(currentColor.dataset.colorName);
            this.updateURL();
            await this.updateVariantData();
            await this.updateImages();
            this.updateDescription();
            this.triggerProductSwap();
        } finally {
            this.setVariantSelectsUpdating(false);
        }
    }

    updateURL() {
        let url = this.currentColor.dataset.productUrl; 
        window.history.replaceState({ }, '', `${url}`);
    }

    updateActiveColorLabel(pColorName) {
        this.querySelector("[data-color-container]").textContent = pColorName;
    }

    setCurrentColor() {
        let currentColorName =  this.querySelector("[data-color-container]").textContent.toLocaleLowerCase().replace(/[\n\r]+|[\s]{2,}/g, '');
        this.querySelectorAll('[data-color-option]').forEach((elem) => {
            if(elem.dataset.colorName.toLocaleLowerCase() === currentColorName ) {
                elem.checked = true; 
            }
        });
        this.updateSelectedState();
    }

    updateSelectedState() {
        this.querySelectorAll('.custom-color-picker__color').forEach((colorEl) => {
            const input = colorEl.querySelector('input[data-color-option]');
            if (input?.checked) {
                colorEl.classList.add('is-selected');
            } else {
                colorEl.classList.remove('is-selected');
            }
        });
    }

    getCurrentColor() { 
        let that = this; 
        this.querySelectorAll('[data-color-option]').forEach((elem) => {
            if(elem.checked) {
                this.currentColor = elem; 
            }
        }); 

        return this.currentColor; 
    }; 

    
    async updateVariantData() {
        const currentColor = this.getCurrentColor(); 
        const domSectionId = this.getDomSectionId();
        const fetchSectionId = this.getFetchSectionId();
        const productObj = JSON.parse(currentColor.dataset.product);
        const productContainer =
            this.closest('product-info') ||
            this.getProductScope().querySelector(
                `[data-product-container][data-section="${domSectionId}"]`
            );
        
        if (!productContainer) {
            console.warn('Product container not found');
            return;
        }
        
        // Update product-info dataset.url so variant-selects can fetch the correct variant
        productContainer.dataset.url = currentColor.dataset.productUrl;
        
        // Fetch the product section HTML to get updated option value IDs
        const productUrl = currentColor.dataset.productUrl;
        const requestUrl = `${productUrl}?section_id=${fetchSectionId}`;

        try {
            const response = await fetch(requestUrl);
            const responseText = await response.text();
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newVariantSelects = html.querySelector(`variant-selects[data-section="${fetchSectionId}"]`);
            
            if (!newVariantSelects) {
                console.warn('New variant-selects not found in fetched HTML');
                return;
            }
            
            // Get the first available variant
            const firstVariant = productObj.variants.find(v => v.available) || productObj.variants[0];
            
            // Update variant-selects elements  
            productContainer.querySelectorAll(`variant-selects[data-section="${domSectionId}"]`).forEach((oldVariantSelects) => {
                // Update the product URL
                oldVariantSelects.dataset.url = productUrl;

                const productInfo = this.closest('product-info');
                const requiresSizeSelection =
                    productInfo &&
                    window.SizeSelection?.requiresSelection(productInfo);

                this.updateSelectedVariantScript(
                    oldVariantSelects,
                    firstVariant,
                    requiresSizeSelection
                );

                // Update data-option-value-id attributes on existing elements
                this.updateOptionValueIds(oldVariantSelects, newVariantSelects, productObj.variants);

                // Mirror server-rendered availability/disabled state from the fetch
                this.syncVariantOptionAvailability(oldVariantSelects, newVariantSelects);

                this.resetSizeSelectionAfterColorChange(oldVariantSelects);

                oldVariantSelects.dispatchEvent(
                    new CustomEvent('variant-selects:updated', {
                        bubbles: true,
                        detail: { sectionId: domSectionId },
                    })
                );
            });
            
            // Update variant-radios elements
            const newVariantRadios = html.querySelector(`variant-radios[data-section="${fetchSectionId}"]`);
            if (newVariantRadios) {
                productContainer.querySelectorAll(`variant-radios[data-section="${domSectionId}"]`).forEach((oldVariantRadios) => {
                    this.updateVariantsDataScript(oldVariantRadios, productObj.variants);

                    // Update data-option-value-id attributes on existing elements
                    this.updateOptionValueIds(oldVariantRadios, newVariantRadios, productObj.variants);

                    this.syncVariantOptionAvailability(oldVariantRadios, newVariantRadios);
                });
            }
        } catch (error) {
            console.error('Error fetching product section:', error);
        }
    }

    setVariantSelectsUpdating(isUpdating) {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        productInfo.toggleAttribute('data-variant-selects-updating', isUpdating);
    }

    updateOptionValueIds(oldElement, newElement, variants = []) {
        // Create a map of value -> data-option-value-id from the new element
        const valueToIdMap = new Map();
        
        // Map option elements (for dropdowns)
        newElement.querySelectorAll('option[data-option-value-id]').forEach(option => {
            valueToIdMap.set(option.value, {
                optionValueId: option.dataset.optionValueId,
                productUrl: option.dataset.productUrl
            });
        });
        
        // Map radio/input elements (for buttons and swatches)
        newElement.querySelectorAll('input[type="radio"][data-option-value-id]').forEach(input => {
            valueToIdMap.set(input.value, {
                optionValueId: input.dataset.optionValueId,
                productUrl: input.dataset.productUrl
            });
        });
        
        // Update existing option elements
        oldElement.querySelectorAll('option').forEach(option => {
            const newData = valueToIdMap.get(option.value);
            if (newData) {
                option.dataset.optionValueId = newData.optionValueId;
                if (newData.productUrl) {
                    option.dataset.productUrl = newData.productUrl;
                }
            }
        });
        
        // Update existing radio/input elements
        oldElement.querySelectorAll('input[type="radio"]').forEach(input => {
            const newData = valueToIdMap.get(input.value);
            if (newData) {
                input.dataset.optionValueId = newData.optionValueId;
                if (newData.productUrl) {
                    input.dataset.productUrl = newData.productUrl;
                }
            }
        });
    }

    updateSelectedVariantScript(variantSelects, variant, requiresSizeSelection) {
        let script = variantSelects.querySelector('[data-selected-variant]');

        if (!script) {
            script = document.createElement('script');
            script.type = 'application/json';
            script.setAttribute('data-selected-variant', '');
            variantSelects.appendChild(script);
        }

        if (requiresSizeSelection) {
            script.textContent = '';
        } else if (variant) {
            script.textContent = JSON.stringify(variant);
        } else {
            script.textContent = '';
        }
    }

    updateVariantsDataScript(element, variants) {
        let script = element.querySelector(
            'script[type="application/json"]:not([data-selected-variant])'
        );

        if (!script) {
            script = document.createElement('script');
            script.type = 'application/json';
            element.appendChild(script);
        }

        script.textContent = JSON.stringify(variants);
    }

    syncVariantOptionAvailability(current, incoming) {
        current
            .querySelectorAll('fieldset.product-form__input--pill')
            .forEach((fieldset, index) => {
                const incomingFieldset = incoming.querySelectorAll(
                    'fieldset.product-form__input--pill'
                )[index];
                if (!incomingFieldset) return;

                fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
                    const incomingRadio = [
                        ...incomingFieldset.querySelectorAll('input[type="radio"]'),
                    ].find(input => input.value === radio.value);
                    if (!incomingRadio) return;

                    radio.disabled = incomingRadio.disabled;
                    radio.checked = incomingRadio.checked;
                    radio.classList.toggle(
                        'disabled',
                        incomingRadio.classList.contains('disabled')
                    );

                    if (incomingRadio.hasAttribute('aria-disabled')) {
                        radio.setAttribute(
                            'aria-disabled',
                            incomingRadio.getAttribute('aria-disabled')
                        );
                    } else {
                        radio.removeAttribute('aria-disabled');
                    }

                    if (
                        radio.parentElement?.classList.contains(
                            'product-form__input--pill'
                        )
                    ) {
                        radio.parentElement.classList.toggle(
                            'disabled',
                            incomingRadio.disabled ||
                                incomingRadio.classList.contains('disabled') ||
                                incomingRadio.getAttribute('aria-disabled') === 'true'
                        );
                    }

                    radio.parentElement?.classList.remove('is-sold-out');
                });
            });

        current.querySelectorAll('select.select__select').forEach((select, index) => {
            const incomingSelect = incoming.querySelectorAll('select.select__select')[
                index
            ];
            if (!incomingSelect) return;

            [...select.options].forEach(option => {
                const incomingOption = [...incomingSelect.options].find(
                    nextOption => nextOption.value === option.value
                );
                if (!incomingOption) return;

                option.disabled = incomingOption.disabled;
                option.selected = incomingOption.selected;
            });
        });
    }
    resetSizeSelectionAfterColorChange(variantSelectsElement) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !window.SizeSelection?.requiresSelection(productInfo)) {
            return;
        }

        window.SizeSelection.resetSizeSelection(productInfo);
        window.SizeSelection.updateBuyButtons(productInfo);
    }

    getProductScope() {
        return this.closest('product-info') || this.closest('quick-add-modal') || this.closest('quick-add-modal-mobile') || document;
    }

    getDomSectionId() {
        return this.dataset.section;
    }

    getFetchSectionId() {
        const productInfo = this.closest('product-info');
        if (productInfo?.dataset.originalSection) {
            return productInfo.dataset.originalSection;
        }

        return this.dataset.section;
    }

    isInQuickAddModal() {
        return Boolean(this.closest('quick-add-modal') || this.closest('quick-add-modal-mobile'));
    }

    getMediaGallery() {
        return this.getProductScope().querySelector('media-gallery');
    }

    getGalleryImages(productObj) {
        const media = productObj.media || [];
        if (media.length) {
            return media.filter(
                (item) => !item.media_type || item.media_type === 'image'
            );
        }

        return (productObj.images || []).map((src, index) => ({
            src: typeof src === 'string' ? src : src?.src,
            alt: productObj.title,
            media_type: 'image',
            position: index + 1,
        }));
    }

    getMediaSrc(media) {
        return media.src || media.preview_image?.src || '';
    }

    async updateImages() {
        const currentColor = this.getCurrentColor();
        if (!currentColor) return;

        // Quick add configures the gallery as a slideshow on load; keep that
        // structure and only swap slide images instead of replacing the gallery.
        if (this.isInQuickAddModal()) {
            this.updateImagesFromProductData();
            this.dispatchEvent(
                new CustomEvent('media-gallery:updated', {
                    bubbles: true,
                    detail: { sectionId: this.dataset.section },
                })
            );
            return;
        }

        const fetchSectionId = this.getFetchSectionId();
        const productUrl = currentColor.dataset.productUrl;
        const currentGallery = this.getMediaGallery();

        if (productUrl && fetchSectionId && currentGallery) {
            try {
                const response = await fetch(
                    `${productUrl}?section_id=${fetchSectionId}`
                );
                if (response.ok) {
                    const html = new DOMParser().parseFromString(
                        await response.text(),
                        'text/html'
                    );
                    const fetchedGallery =
                        html.querySelector(`#MediaGallery-${fetchSectionId}`) ||
                        html.querySelector('media-gallery');

                    if (fetchedGallery) {
                        currentGallery.replaceWith(fetchedGallery);
                        window.theme?.reinitImageAnimation?.();
                        document.dispatchEvent(
                            new CustomEvent('media-gallery:updated', {
                                bubbles: true,
                                detail: { sectionId: this.getDomSectionId() },
                            })
                        );
                        return;
                    }
                }
            } catch (error) {
                console.error('Error fetching product gallery:', error);
            }
        }

        this.updateImagesFromProductData();
    }

    updateImagesFromProductData() {
        const productObj = JSON.parse(this.currentColor.dataset.product);
        const images = this.getGalleryImages(productObj);

        if (!images.length) {
            return;
        }

        const mediaGallery = this.getMediaGallery();
        if (!mediaGallery) return;

        const mainSlideshow =
            mediaGallery.querySelector('.product-media-gallery__main') ||
            mediaGallery.querySelector('.product-media-gallery__grid-host');
        const thumbsSlideshow = mediaGallery.querySelector(
            '.product-media-gallery__thumbnails'
        );

        if (!mainSlideshow) return;

        const existingMedia = mainSlideshow.querySelector('.product__media');
        let aspectRatio = '9/13';
        if (existingMedia) {
            const style = existingMedia.getAttribute('style');
            const match = style?.match(/aspect-ratio:\s*([^;]+)/);
            if (match) aspectRatio = match[1].trim();
        }

        const processImageSrc = (src, size) => {
            if (!src) return '';

            const width = parseInt(String(size).replace(/x$/i, ''), 10);
            if (!Number.isFinite(width) || width <= 0) return src;

            try {
                const normalized = src.startsWith('//') ? `https:${src}` : src;
                const url = new URL(normalized, window.location.origin);
                url.searchParams.set('width', String(width));
                url.searchParams.delete('height');
                return url.toString();
            } catch {
                return src
                    .replace(/(\.[^.]*)$/, `_${size}$1`)
                    .replace(/^http:/, '');
            }
        };

        this.updateSwiperSlides(
            mainSlideshow,
            images,
            processImageSrc,
            'main',
            aspectRatio
        );

        if (thumbsSlideshow) {
            this.updateSwiperSlides(
                thumbsSlideshow,
                images,
                processImageSrc,
                'thumb',
                aspectRatio
            );
            this.reconnectSwipers(mainSlideshow, thumbsSlideshow);
        } else {
            this.reinitializeSlideshow(mainSlideshow);
        }
    }

    getSlideContainer(slideshowElement) {
        const sliderContainer = slideshowElement.querySelector(
            '[data-swiper-slideshow]'
        );
        if (!sliderContainer) return null;

        return (
            sliderContainer.querySelector('.swiper-wrapper') || sliderContainer
        );
    }

    clearSlideContainer(slideContainer) {
        Array.from(slideContainer.children).forEach((child) => {
            if (
                child.matches(
                    '[data-swiper-slide], .swiper-slide, .product-media-gallery__grid-item'
                )
            ) {
                child.remove();
            }
        });
    }

    reinitializeSlideshow(slideshowElement) {
        if (typeof slideshowElement.destroy === 'function') {
            slideshowElement.destroy();
        }

        if (typeof slideshowElement.syncLayoutMode === 'function') {
            slideshowElement.syncLayoutMode();
            return;
        }

        const swiperInstance =
            slideshowElement.swiper || slideshowElement.getSwiper?.();
        if (swiperInstance) {
            swiperInstance.update();
            swiperInstance.slideTo(0, 0);
        }
    }

    updateSwiperSlides(slideshowElement, images, processImageSrc, type, aspectRatio) {
        const slideContainer = this.getSlideContainer(slideshowElement);
        if (!slideContainer) return;

        const isGridHost = slideshowElement.classList.contains(
            'product-media-gallery__grid-host'
        );

        this.clearSlideContainer(slideContainer);

        // Create new slides
        images.forEach((media, index) => {
            const mediaSrc = this.getMediaSrc(media);
            if (!mediaSrc) return;

            const slide = document.createElement('div');
            slide.className = isGridHost
                ? 'swiper-slide product-media-gallery__grid-item product__media-item-wrapper'
                : 'swiper-slide';
            slide.setAttribute('data-swiper-slide', '');
            
            if (type === 'main') {
                const zoomMaxWidth =
                    media?.preview_image?.width || media?.width || 5760;
                const zoomSrc = processImageSrc(mediaSrc, zoomMaxWidth) || mediaSrc;
                slide.innerHTML = `
                    <div class="product__media-item-wrapper">
                        <div class="product__media media media--transparent global-media-settings image-magnify-hover" data-zoom-image data-zoom-src="${zoomSrc}" data-zoom-max-width="${zoomMaxWidth}" style="aspect-ratio: ${aspectRatio};">
                            <img
                                srcset="${processImageSrc(mediaSrc, 493)} 493w,
                                        ${processImageSrc(mediaSrc, 600)} 600w,
                                        ${processImageSrc(mediaSrc, 713)} 713w,
                                        ${processImageSrc(mediaSrc, 823)} 823w,
                                        ${processImageSrc(mediaSrc, 990)} 990w,
                                        ${processImageSrc(mediaSrc, 1100)} 1100w,
                                        ${processImageSrc(mediaSrc, 1206)} 1206w,
                                        ${processImageSrc(mediaSrc, 1346)} 1346w,
                                        ${processImageSrc(mediaSrc, 1426)} 1426w,
                                        ${processImageSrc(mediaSrc, 1646)} 1646w,
                                        ${processImageSrc(mediaSrc, 1946)} 1946w,
                                        ${processImageSrc(mediaSrc, zoomMaxWidth)} ${zoomMaxWidth}w"
                                src="${processImageSrc(mediaSrc, 1946)}"
                                sizes="(min-width: 1200px) 50vw, (min-width: 990px) calc(50vw - 10rem), (min-width: 700px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)"
                                loading="${index === 0 ? 'eager' : 'lazy'}"
                                class="image-magnify-hover"
                                alt="${media.alt || ''}"
                                width="973"
                                height="973"
                                data-image-loading-animation
                            >
                        </div>
                    </div>
                `;
            } else if (type === 'thumb') {
                slide.innerHTML = `
                    <div class="product__media-thumbnail-wrapper">
                        <div class="product__media media media--transparent global-media-settings" style="aspect-ratio: ${aspectRatio};">
                            <img
                                srcset="${processImageSrc(mediaSrc, 493)} 493w,
                                        ${processImageSrc(mediaSrc, 600)} 600w,
                                        ${processImageSrc(mediaSrc, 713)} 713w"
                                src="${processImageSrc(mediaSrc, 600)}"
                                sizes="(min-width: 990px) calc(50vw - 10rem), (min-width: 700px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)"
                                alt="${media.alt || ''}"
                                loading="${index === 0 ? 'eager' : 'lazy'}"
                                width="600"
                                height="600"
                            >
                        </div>
                    </div>
                `;
            }
            
            slideContainer.appendChild(slide);
        });

        this.reinitializeSlideshow(slideshowElement);
        window.theme?.reinitImageAnimation?.();
    }

    reconnectSwipers(mainSlideshow, thumbsSlideshow) {
        setTimeout(() => {
            const mainSwiper = mainSlideshow.swiper;
            const thumbsSwiper = thumbsSlideshow.swiper;

            if (mainSwiper && thumbsSwiper) {
                mainSwiper.params.thumbs = { swiper: thumbsSwiper };
                if (mainSwiper.thumbs) {
                    mainSwiper.thumbs.init();
                    mainSwiper.thumbs.update();
                }

                mainSwiper.on('slideChange', function () {
                    thumbsSwiper.slideTo(mainSwiper.activeIndex);
                });

                thumbsSwiper.on('click', function (swiper) {
                    if (typeof swiper.clickedIndex !== 'undefined') {
                        mainSwiper.slideTo(swiper.clickedIndex);
                    }
                });

                mainSwiper.update();
                thumbsSwiper.update();
            }
        }, 100);
    }
    
    updateDescription() {
        let productObj = JSON.parse(this.currentColor.dataset.product); 
        let descriptionContainer = document.querySelector('[data-product-description-container]'); 
        if (descriptionContainer) {
            descriptionContainer.innerHTML = productObj.description; 
        }
    }

    triggerProductSwap() {
        const productObj = JSON.parse(this.currentColor.dataset.product);
        const sectionId = this.dataset.section;
        const productInfo = this.closest('product-info');
        
        let firstVariant = null;
        if (productObj.variants && productObj.variants.length > 0) {
            firstVariant = productObj.variants.find(v => v.available) || productObj.variants[0];
        }
        
        if (!firstVariant) return;

        this.updateProductDetails(firstVariant, productObj);

        if (productInfo && window.SizeSelection?.requiresSelection(productInfo)) {
            window.SizeSelection.updateBuyButtons(productInfo);
        } else {
            this.updateProductForm(firstVariant.id);
            this.updateBuyButton(firstVariant);
        }
        
        this.dispatchEvent(new CustomEvent('color-picker:product-changed', {
            detail: {
                product: productObj,
                variant: firstVariant,
                sectionId: sectionId
            },
            bubbles: true
        }));
    }

    updateProductDetails(variant, product) {
        const sectionId = this.dataset.section;
        const scope = this.getProductScope();
        
        // Update price
        const priceElement = scope.querySelector(`#price-${sectionId}`);
        if (priceElement && variant.price) {
            const priceHTML = this.formatPrice(variant.price, variant.compare_at_price);
            if (priceHTML) {
                priceElement.innerHTML = priceHTML;
            }
        }

        // Update SKU
        const skuElement = scope.querySelector(`#Sku-${sectionId}`);
        if (skuElement && variant.sku) {
            skuElement.textContent = variant.sku;
            skuElement.classList.remove('visibility-hidden');
        } else if (skuElement) {
            skuElement.classList.add('visibility-hidden');
        }

        // Update inventory if exists
        const inventoryElement = scope.querySelector(`#Inventory-${sectionId}`);
        if (inventoryElement && variant.inventory_management === 'shopify') {
            // Update inventory display if needed
            if (variant.inventory_quantity > 0) {
                inventoryElement.classList.remove('visibility-hidden');
            }
        }

        // Update product title
        const titleElement = scope.querySelector('.product__title');
        if (titleElement && product.title) {
            titleElement.textContent = product.title;
        }
    }

    formatPrice(price, compareAtPrice) {
        const formattedPrice = window.theme.formatMoney(price);
        const formattedCompareAtPrice = compareAtPrice
            ? window.theme.formatMoney(compareAtPrice)
            : null;

        let priceHTML = `
            <div class="price">
                <div class="price__container">
                    <div class="price__regular">
                        <span class="price-item price-item--regular">
                            ${formattedPrice}
                        </span>
                    </div>
        `;
        
        if (formattedCompareAtPrice && compareAtPrice > price) {
            priceHTML += `
                    <div class="price__sale">
                        <span class="price-item price-item--sale">
                            ${formattedPrice}
                        </span>
                        <s class="price-item price-item--regular">
                            ${formattedCompareAtPrice}
                        </s>
                    </div>
            `;
        }
        
        priceHTML += `
                </div>
            </div>
        `;
        
        return priceHTML;
    }

    updateProductForm(variantId) {
        const sectionId = this.dataset.section;
        const scope = this.getProductScope();
        
        // Update all product forms (main form and installment form)
        const productForms = scope.querySelectorAll(
            `#product-form-${sectionId}, #product-form-installment-${sectionId}`
        );
        
        productForms.forEach(form => {
            const variantInput = form.querySelector('input[name="id"]');
            if (variantInput) {
                variantInput.value = variantId;
                variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
        
        // Also update the product-form element's internal state
        const productFormElement = scope.querySelector(`product-form[data-section-id="${sectionId}"]`);
        if (productFormElement) {
            const hiddenInput = productFormElement.querySelector('input[name="id"]');
            if (hiddenInput) {
                hiddenInput.value = variantId;
                hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    updateBuyButton(variant) {
        const sectionId = this.dataset.section;
        
        // Find the product-form using data-section-id
        const productForm = this.getProductScope().querySelector(
            `product-form[data-section-id="${sectionId}"]`
        );
        
        if (!productForm) {
            console.warn('Product form not found');
            return;
        }
        
        const submitButton = productForm.querySelector('button[type="submit"]');
        const submitButtonText = submitButton?.querySelector('span');
        
        if (!submitButton || !submitButtonText) {
            console.warn('Submit button not found');
            return;
        }
        
        const productInfo = this.closest('product-info');
        if (productInfo && window.SizeSelection?.requiresSelection(productInfo)) {
            window.SizeSelection.updateBuyButtons(productInfo);
            return;
        }

        // Update button state based on variant availability
        if (variant.available) {
            submitButton.removeAttribute('disabled');
            submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
            submitButton.setAttribute('disabled', 'disabled');
            submitButtonText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
        
        // Update hidden input in the form
        const hiddenInput = productForm.querySelector('input[name="id"]');
        if (hiddenInput) {
            hiddenInput.value = variant.id;
            if (!variant.available) {
                hiddenInput.setAttribute('disabled', 'disabled');
            } else {
                hiddenInput.removeAttribute('disabled');
            }
        }
    }

    setUpEvents() {
        if(this.querySelectorAll("[data-color-container]").length > 0) {
            let colorContainer = this.querySelector('[data-color-container]');
            let currentColor = this.querySelector("[data-color-container]").textContent; 
    
            function showColor(pColor) {
                colorContainer.textContent = pColor; 
            }
        
            this.querySelectorAll('[data-color-label]').forEach((element) => {
                element.addEventListener('mouseenter', function(event) {
                    let name = this.dataset.colorName; 
                    showColor(name)
                })
                element.addEventListener('mouseleave', (event) => {
                    showColor(this.getCurrentColor().dataset.colorName)
                })
            }); 
        
        }
    }

    init() {
        this.setUpEvents(); 
        this.setCurrentColor();
        this.dispatchEvent(
            new CustomEvent('custom-color-picker:ready', {
                bubbles: true,
                detail: { sectionId: this.dataset.section },
            })
        );
    }
}

customElements.define('custom-color-picker', CustomColorPicker); 